const DEFAULT_CONFIG = {
    apiUri: 'wss://cpclientapi.softphone.com:9002/counterpath/socketapi/v1/',
    userAgentString: 'PhoneBooth',
    xmlDeclarationString: '<?xml version="1.0" encoding="utf-8" ?>\r\n',
};

export default class BriaAPI {
    constructor(conf) {
        this.websocket;
        this.lastTransactionID = 0;
        this.incomingMessage;
        this.loggedIn = false;

        this.config = Object.assign({}, DEFAULT_CONFIG, conf || {});
    }

    init() {
        this.connectToWebSocket();
    }

    connectToWebSocket() {
        let connectionError = false;

        /* Create new WebSocket object */
        try {
            this.websocket = new WebSocket(this.config.apiUri);
        } catch (e) {
            console.log('WebSocket exception: ' + e);
        }

        /* Event handler for connection established */
        this.websocket.onopen = (evt) => {
            connectionError = false;
            this.setConnectionStatus('CONNECTED');
            this.onConnectedToWebSocket();
        };

        /* Event handler for disconnection from Web-Socket */
        this.websocket.onclose = (evt) => {
            if (!connectionError) {
                this.setConnectionStatus('DISCONNECTED - retrying connection after 5 seconds ...');
            }
            console.log('OnClose: ' + evt.code);

            /* Set timer to attempt re-connection in 5 seconds */
            setTimeout(() => {
                this.connectToWebSocket();
            }, 5000);
        };

        /* Event handler for Errors on the Web-Socket connection */
        this.websocket.onerror = (evt) => {
            connectionError = true;
            this.setConnectionStatus('ERROR Could not connect to WebSocket - retry will happen after 5 seconds ...');
            console.log('OnError: ' + evt.name);
            this.websocket.close();
        };

        /* Event handler for received messages via web-socket connection */
        this.websocket.onmessage = (evt) => {
            //console.info('--- RECEIVED ---\n' + evt.data);

            this.processApiMessageReceived(evt.data);
        };
    }

    setConnectionStatus(status) {
        console.log('setting status to: ' + status);
    }
    /****************************************************************************
                      WEB SOCKET CONNECTION HANDLING
 	****************************************************************************/
    onConnectedToWebSocket() {
        /* Do some stuff here when the connection is first established ... */

        this.apiGetStatus(ApiStatusEventTypes.properties[ApiStatusEventTypes.AUTHENTICATION].text);

        /* Request CALL status to sync the list of active calls */
        this.apiGetStatus(ApiStatusEventTypes.properties[ApiStatusEventTypes.CALL].text);
    }

    /****************************************************************************
                             BRIA API COMMANDS
 	****************************************************************************/
    apiPlaceCall(target, suppressGUI = true) {
        const content =
            this.config.xmlDeclarationString +
            '<dial type="audio">\r\n <number>' +
            target +
            '</number>\r\n <displayName></displayName>\r\n <suppressMainWindow>' +
            suppressGUI +
            '</suppressMainWindow>\r\n</dial>';
        const msg = this.constructApiMessage(ApiRequestTypes.CALL, content);
        this.sendMessage(msg);
    }

    apiEndCall(callId) {
        const content =
            this.config.xmlDeclarationString + '<endCall>\r\n <callId>' + callId + '</callId>\r\n</endCall>';
        const msg = this.constructApiMessage(ApiRequestTypes.ENDCALL, content);
        this.sendMessage(msg);
    }

    apiGetStatus(statusType) {
        const content = this.config.xmlDeclarationString + '<status>\r\n <type>' + statusType + '</type>\r\n</status>';
        const msg = this.constructApiMessage(ApiRequestTypes.STATUS, content);
        this.sendMessage(msg);
    }

    apiGetStatusWithParameters(statusType, parameterXML) {
        const content =
            this.config.xmlDeclarationString +
            '<status>\r\n <type>' +
            statusType +
            '</type>\r\n' +
            parameterXML +
            '</status>';
        const msg = this.constructApiMessage(ApiRequestTypes.STATUS, content);
        this.sendMessage(msg);
    }

    apiSendKey(key) {
        const content = this.config.xmlDeclarationString + `<dtmf>\r\n <tone digit="${key}">start</tone>\r\n</dtmf>`;
        const msg = this.constructApiMessage(ApiRequestTypes.DTMF, content);
        this.sendMessage(msg);
    }

    /****************************************************************************
                          API MESSAGE CONSTRUCTION
 	****************************************************************************/

    getNextTransactionID() {
        this.lastTransactionID++;
        return this.lastTransactionID;
    }

    constructApiMessage(requestType, body) {
        const contentLength = body.length;
        let msg =
            'GET /' +
            ApiRequestTypes.properties[requestType].text +
            '\r\nUser-Agent: ' +
            this.config.userAgentString +
            '\r\nTransaction-ID: ' +
            this.getNextTransactionID() +
            '\r\nContent-Type: application/xml\r\nContent-Length: ' +
            contentLength;

        if (contentLength > 0) msg += '\r\n\r\n' + body;

        return msg;
    }

    sendMessage(msg) {
        this.websocket.send(msg);
        //console.log('----- SENT -----\n' + msg);
    }

    processApiMessageReceived(msg) {
        this.incomingMessage = this.parseMessage(msg);
        switch (this.incomingMessage.messageType) {
            case ApiMessageTypes.RESPONSE:
                /* This is a response to a status query */
                this.processResponse(this.incomingMessage.content);
                break;

            case ApiMessageTypes.EVENT:
                /* Check the event type */
                if (this.incomingMessage.eventType === ApiEventTypes.STATUSCHANGE) {
                    /* Handle the Status Change Event */
                    this.processStatusChangeEvent(this.incomingMessage.content);
                } else {
                    /* Unknown event type received */
                    console.log('Unknown Event Type received ' + this.incomingMessage.eventType);
                }
                break;

            case ApiMessageTypes.ERROR:
                /* Handle Error from API */
                console.log(
                    'API Returned Error: ' + this.incomingMessage.errorCode + ' - ' + this.incomingMessage.errorText,
                );
                break;

            default:
                /* Unknown message type received */
                console.log('Unknown API Message Type received');
                break;
        }
    }

    processResponse(responseXML) {
        /* Basic responses carry no content, so we don't need to process them */
        /* For responses with XML data, figure out the type and distribute to sub-processing functions */
        if (responseXML.length > 0) {
            const parser = new DOMParser();
            const xmlDoc = parser.parseFromString(responseXML, 'text/xml');

            const statusElement = xmlDoc.getElementsByTagName('status')[0];
            if (statusElement != null) {
                const statusType = statusElement.getAttributeNode('type');
                console.log('Status Response type: ' + statusType.nodeValue);

                switch (statusType.nodeValue) {
                    case ApiStatusEventTypes.properties[ApiStatusEventTypes.CALL].text:
                        this.handleCallStatusResponse(xmlDoc);
                        break;
                    case ApiStatusEventTypes.properties[ApiStatusEventTypes.AUTHENTICATION].text:
                        this.handleAuthenticationStatusResponse(xmlDoc);
                        break;

                    default:
                        console.log('Unknown Response type: ' + responseXML);
                        break;
                }
            }
        }
    }

    processStatusChangeEvent(eventXML) {
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(eventXML, 'text/xml');

        const eventElement = xmlDoc.getElementsByTagName('event')[0];
        const eventType = eventElement.getAttributeNode('type');

        this.handleStatusChangeEvent(eventType.nodeValue);
    }

    /****************************************************************************
							 BRIA API MESSAGE PARSING
	****************************************************************************/

    parseMessage(msg) {
        let messageType = ApiMessageTypes.UNKNOWN;
        let eventType = ApiEventTypes.UNKNOWN;
        let errorCode = 0;
        let errorText = '';

        let transactionId = '';
        let userAgentString = '';
        let contentType = '';
        let contentLength = 0;
        let content = '';

        const lines = msg.replace(/\r\n/g, '\n').split('\n');
        let line = lines[0];

        if (line.substr(0, 4) === 'POST') {
            messageType = ApiMessageTypes.EVENT;
            line = line.substr(5).trim();
            if (line.substr(0, 13) === '/statusChange') {
                eventType = ApiEventTypes.STATUSCHANGE;
            }
        } else if (line.substr(0, 8) === 'HTTP/1.1') {
            line = line.substr(8).trim();
            if (line.substr(0, 6) === '200 OK') {
                messageType = ApiMessageTypes.RESPONSE;
            } else if (line[0] === '4' || line[0] === '5') {
                messageType = ApiMessageTypes.ERROR;
                errorCode = line.substr(0, 3);
                errorText = line.substr(4);
            }
        }

        /* Parse the remaining lines in the header and extract values */
        let i = 1;
        for (; i < lines.length; i++) {
            line = lines[i];

            if (line[0] === '<') {
                /* Start of the content section */
                break;
            } else if (line.substr(0, 15) === 'Transaction-ID:') {
                transactionId = line.substr(15).trim();
            } else if (line.substr(0, 11) === 'User-Agent:') {
                userAgentString = line.substr(11).trim();
            } else if (line.substr(0, 13) === 'Content-Type:') {
                contentType = line.substr(13).trim();
            } else if (line.substr(0, 15) === 'Content-Length:') {
                contentLength = Number(line.substr(15).trim());
            } else {
                /* Ignore any unknown headers */
                continue;
            }
        }

        /* Re-assemble the content portion from the remaining lines */
        for (; i < lines.length; i++) {
            content += lines[i];
            if (i < lines.length - 1) {
                content += '\n';
            }
        }

        /* Return object with all the details from the message */
        return {
            messageType: messageType,
            eventType: eventType,
            errorCode: errorCode,
            errorText: errorText,
            transactionId: transactionId,
            userAgentString: userAgentString,
            contentType: contentType,
            contentLength: contentLength,
            content: content,
        };
    }

    /****************************************************************************
                          BRIA API EVENT HANDLING
 	****************************************************************************/
    getStringValueFromXMLTag(xmlItem, tagName) {
        const element = xmlItem.getElementsByTagName(tagName)[0];

        if (element != null) {
            const childNode = element.childNodes[0];
            if (childNode != null) return childNode.nodeValue;
        }
        return '';
    }

    handleStatusChangeEvent(eventType) {
        console.log('Status Change Event - type: ' + eventType);

        switch (eventType) {
            case ApiStatusEventTypes.properties[ApiStatusEventTypes.AUTHENTICATION].text:
            case ApiStatusEventTypes.properties[ApiStatusEventTypes.CALL].text:
                this.apiGetStatus(eventType);
                break;
        }
    }
    handleCallStatusResponse(callStatusDoc) {
        this.currentCallList = [];
        const calls = callStatusDoc.getElementsByTagName('call');

        for (let i = 0; i < calls.length; i++) {
            const call = calls[i];
            const id = this.getStringValueFromXMLTag(call, 'id');
            const hold = this.getStringValueFromXMLTag(call, 'holdStatus');

            const participants = call.getElementsByTagName('participants')[0].getElementsByTagName('participant');

            const participantList = [];

            for (let p = 0; p < participants.length; p++) {
                const participant = participants[p];

                const number = this.getStringValueFromXMLTag(participant, 'number');
                const displayName = this.getStringValueFromXMLTag(participant, 'displayName');
                const state = this.getStringValueFromXMLTag(participant, 'state');
                const startTime = this.getStringValueFromXMLTag(participant, 'timeInitiated');
                const participantInfo = {
                    number: number,
                    displayName: displayName,
                    state: state,
                    startTime: startTime,
                };
                participantList.push(participantInfo);
            }

            this.currentCallList.push({
                id: id,
                hold: hold,
                participants: participantList,
            });
        }
        this.updateCallActivity();
    }

    handleAuthenticationStatusResponse(authenticationStatusDoc) {
        const authenticated = this.getStringValueFromXMLTag(authenticationStatusDoc, 'authenticated');
        console.log('Logged in: ' + authenticated);

        if (authenticated === 'true') {
            console.log('Client logged in');
        } else {
            /* Client is logged out */
            const failedReason = this.getStringValueFromXMLTag(authenticationStatusDoc, 'notAuthenticatedReason');
            const serverReason = this.getStringValueFromXMLTag(authenticationStatusDoc, 'serverProvidedReason');

            console.log('Client logged out due to: ' + failedReason);
            console.log('Server provided reason: ' + serverReason);
        }
    }

    updateCallActivity() {
        for (let i = 0; i < this.currentCallList.length; i++) {
            const call = this.currentCallList[i];

            if (call.participants.length === 1) {
                const participant = call.participants[0];
                switch (participant.state) {
                    case ApiCallStates.properties[ApiCallStates.RINGING].text:
                    case ApiCallStates.properties[ApiCallStates.CONNECTING].text:
                        this.config.events[ApiCallStates.CONNECTING] &&
                            this.config.events[ApiCallStates.CONNECTING](call, participant);
                        break;
                    case ApiCallStates.properties[ApiCallStates.CONNECTED].text:
                        this.config.events[ApiCallStates.CONNECTED] &&
                            this.config.events[ApiCallStates.CONNECTED](call, participant);
                        break;
                    case ApiCallStates.properties[ApiCallStates.FAILED].text:
                    case ApiCallStates.properties[ApiCallStates.ENDED].text:
                        this.config.events[ApiCallStates.ENDED] &&
                            this.config.events[ApiCallStates.ENDED](call, participant);
                        break;
                    default:
                        break;
                }
            }
        }
        if (this.currentCallList.length === 0) {
            this.config.events[ApiCallStates.ENDED] && this.config.events[ApiCallStates.ENDED]();
        }
    }
}

/****************************************************************************
                        API REQUEST TYPES
  ****************************************************************************/

const ApiRequestTypes = {
    BRINGTOFRONT: 0,
    SHOWHISTORY: 1,
    STATUS: 2,
    SELECTAUDIODEVICES: 3,
    CALL: 4,
    ANSWER: 5,
    HOLD: 6,
    RESUME: 7,
    DTMF: 8,
    TRANSFERCALL: 9,
    AUDIOPROPERTIES: 10,
    CALLOPTIONS: 11,
    ENDCALL: 12,
    STARTCALLRECORDING: 13,
    STOPCALLRECORDING: 14,
    CHECKVOICEMAIL: 15,
    SETPRESENCE: 16,
    IM: 17,
    STARTSCREENSHARE: 18,
    SIGNOUT: 19,
    EXIT: 20,
    SIGNIN: 21,

    properties: {
        0: { text: 'bringToFront' },
        1: { text: 'showHistory' },
        2: { text: 'status' },
        3: { text: 'selectAudioDevices' },
        4: { text: 'call' },
        5: { text: 'answer' },
        6: { text: 'hold' },
        7: { text: 'resume' },
        8: { text: 'dtmf' },
        9: { text: 'transferCall' },
        10: { text: 'audioProperties' },
        11: { text: 'callOptions' },
        12: { text: 'endCall' },
        13: { text: 'startCallRecording' },
        14: { text: 'stopCallRecording' },
        15: { text: 'checkVoiceMail' },
        16: { text: 'setPresence' },
        17: { text: 'im' },
        18: { text: 'startScreenShare' },
        19: { text: 'signOut' },
        20: { text: 'exit' },
        21: { text: 'signIn' },
    },
};

/****************************************************************************
                        API RESPONSE TYPES
 ****************************************************************************/

const ApiMessageTypes = {
    UNKNOWN: 0,
    RESPONSE: 1,
    EVENT: 2,
    ERROR: 3,
};

const ApiEventTypes = {
    UNKNOWN: 0,
    STATUSCHANGE: 1,
};

const ApiStatusEventTypes = {
    UNKNOWN: 0,
    PHONE: 1,
    CALL: 2,
    CALLHISTORY: 3,
    MISSEDCALL: 4,
    VOICEMAIL: 5,
    AUDIODEVICES: 6,
    AUDIOPROPERTIES: 7,
    CALLOPTIONS: 8,
    PRESENCE: 9,
    SCREENSHARE: 10,
    AUTHENTICATION: 11,

    properties: {
        1: { text: 'phone' },
        2: { text: 'call' },
        3: { text: 'callHistory' },
        4: { text: 'missedCall' },
        5: { text: 'voicemail' },
        6: { text: 'audioDevices' },
        7: { text: 'audioProperties' },
        8: { text: 'callOptions' },
        9: { text: 'presence' },
        10: { text: 'screenShare' },
        11: { text: 'authentication' },
    },
};

export const ApiCallStates = {
    UNKNOWN: 0,
    RINGING: 1,
    CONNECTING: 2,
    CONNECTED: 3,
    FAILED: 4,
    ENDED: 5,

    properties: {
        1: { text: 'ringing' },
        2: { text: 'connecting' },
        3: { text: 'connected' },
        4: { text: 'failed' },
        5: { text: 'ended' },
    },
};
